import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { InlineAddForm } from '../components/InlineAddForm.jsx';
import {
  DeleteIcon,
  RefreshIcon,
  AddIcon,
  GlobeIcon,
  XIcon,
} from '../components/icons';

/**
 * Sites — origin allow-list for the SDK edge.
 *
 * Scoped to the surrounding project when mounted under
 * /projects/<slug>/sites so the origins narrow the project's
 * allow-list specifically; the tenant's legacy tenant-wide sites
 * still apply across every project.
 */
export function Sites() {
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const projectId = project?.project_id;
  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmNode, ask] = useConfirm();

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api(`/v1/admin/sites?project_id=${encodeURIComponent(projectId)}`);
      setSites(resp?.sites || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Sites</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          Each place you drop the SDK for this project gets a Site
          here. Origins narrow the project's allow-list — requests
          from pages not on the list are rejected at the API edge
          with <code>403 Origin not allowed</code>.
        </p>
      </div>

      <CreateSiteForm api={api} projectId={projectId} onCreated={load} />

      {loading && <div className="state"><div className="state__title">Loading sites…</div></div>}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load sites</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && (
        sites.length === 0 ? (
          <div className="state">
            <div className="state__title">No sites yet</div>
            <div>Add one above to register the first origin. Production, staging, and dev each get their own Site so you can disable one without touching the others.</div>
          </div>
        ) : (
          <ul className="list">
            {sites.map((s) => (
              <SiteRow key={s.site_id} site={s} api={api} ask={ask} onChanged={load} />
            ))}
          </ul>
        )
      )}

      <SeenOriginsPanel api={api} sites={sites} onAllowlistAdded={load} />

      {confirmNode}
    </section>
  );
}

function SeenOriginsPanel({ api, sites, onAllowlistAdded }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api('/v1/admin/sites/seen');
      setRows(resp?.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Origins already on at least one site's allow-list — used to
  // suppress the "Add to allow-list" button when there's nothing
  // for the operator to do.
  const known = useMemo(() => {
    const set = new Set();
    for (const s of sites || []) for (const o of (s.origins || [])) set.add(o);
    return set;
  }, [sites]);

  // Pull rejected origins to the top so an operator sees pages that
  // failed origin lockdown first.  Within each bucket keep the
  // backend's ordering (most-recent first).
  const sortedRows = useMemo(() => {
    const rejected = [];
    const allowed = [];
    for (const r of rows) {
      if (r.last_status === 'rejected' && !known.has(r.origin)) rejected.push(r);
      else allowed.push(r);
    }
    return [...rejected, ...allowed];
  }, [rows, known]);

  const pendingRejectedCount = useMemo(
    () => rows.filter((r) => r.last_status === 'rejected' && !known.has(r.origin)).length,
    [rows, known],
  );

  async function addToFirstSite(origin) {
    const target = sites.find((s) => s.active) || sites[0];
    if (!target) {
      // No site yet — create a "Default" site with this origin.
      setBusy(origin);
      try {
        await api('/v1/admin/sites', {
          method: 'POST',
          body: JSON.stringify({ name: 'Default', origins: [origin] }),
        });
        onAllowlistAdded();
        await load();
      } finally { setBusy(null); }
      return;
    }
    setBusy(origin);
    try {
      const nextOrigins = Array.from(new Set([...(target.origins || []), origin]));
      await api(`/v1/admin/sites/${target.site_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ origins: nextOrigins }),
      });
      onAllowlistAdded();
      await load();
    } finally { setBusy(null); }
  }

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <h3 className="card__title" style={{ margin: 0 }}>Recently seen origins</h3>
        <button type="button" className="btn btn--ghost" onClick={load} disabled={loading}>
          <RefreshIcon /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <p className="page__subtitle" style={{ marginTop: 0, marginBottom: 10 }}>
        Every site the API edge has observed for this tenant —
        whether the request was let through or 403'd at origin
        lockdown. Useful for spotting customer pages that are trying
        to connect but missing from any Site's allow-list.
      </p>

      {pendingRejectedCount > 0 && (
        <div
          className="state state--error"
          style={{ marginBottom: 10, textAlign: 'left' }}
        >
          <div className="state__title">
            {pendingRejectedCount} origin{pendingRejectedCount === 1 ? '' : 's'} blocked at the edge
          </div>
          <div>
            Requests from these pages were rejected by origin
            lockdown.  Each row is either a real customer page that
            still needs to be added to your allow-list, or a
            probe from an origin you don't recognise. Review them
            below — add the ones you trust, ignore the rest.
          </div>
        </div>
      )}

      {error && <div className="state state--error" style={{ marginBottom: 10 }}>{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div className="state">
          <div className="state__title">Nothing observed yet</div>
          <div>Origins appear here the first time a request lands at the API for this tenant. Reload after your SDK has fired a request.</div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="resource-table">
            <thead>
              <tr>
                <th>Origin</th>
                <th>Status</th>
                <th>Count</th>
                <th>Last seen</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => {
                const isRejected = r.last_status === 'rejected';
                const isKnown = known.has(r.origin);
                const isPendingRejected = isRejected && !isKnown;
                return (
                  <tr
                    key={r.origin}
                    style={
                      isPendingRejected
                        ? { background: 'var(--danger-50, #fef2f2)' }
                        : undefined
                    }
                  >
                    <td><code>{r.origin}</code></td>
                    <td>
                      <span className={`badge ${isRejected ? 'badge--default' : 'badge--editor'}`}>
                        {r.last_status}
                      </span>
                      {isPendingRejected && (
                        <span
                          className="badge"
                          style={{ marginLeft: 6, background: 'var(--danger-700, #b91c1c)', color: '#fff' }}
                          title="Origin appeared in a request but no Site allows it"
                        >
                          blocked
                        </span>
                      )}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.count}</td>
                    <td style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                      {formatRelative(r.last_seen)}
                    </td>
                    <td>
                      {isKnown ? (
                        <span style={{ color: 'var(--text-3)', fontSize: 12 }}>on allow-list</span>
                      ) : (
                        <button
                          type="button"
                          className="btn"
                          disabled={busy === r.origin}
                          onClick={() => addToFirstSite(r.origin)}
                        >
                          <AddIcon /> {busy === r.origin ? 'Adding…' : 'Add to allow-list'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

/**
 * Existing "Register a new site" form — kept as a tight two-input
 * surface with the input-baseline button alignment pattern from
 * card 059's InlineAddForm.  Multi-field shape doesn't warrant a
 * wizard (it's "name + initial origin"); per-site origins are added
 * one-at-a-time via the InlineAddForm below each SiteRow.
 */
function CreateSiteForm({ api, projectId, onCreated }) {
  const [name, setName] = useState('');
  const [origins, setOrigins] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api('/v1/admin/sites', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          origins: origins.split(',').map((s) => s.trim()).filter(Boolean),
          project_id: projectId || '',
        }),
      });
      setName('');
      setOrigins('');
      onCreated();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3 className="card__title">Register a new site</h3>
      <form onSubmit={submit} className="site-create">
        <label className="field site-create__field">
          <span className="field__label">Name</span>
          <input
            className="input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Production"
            disabled={busy}
          />
        </label>
        <label className="field site-create__field">
          <span className="field__label">Origins (comma-separated)</span>
          <input
            className="input"
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            placeholder="https://app.acme.com, https://staging.acme.com"
            disabled={busy}
          />
        </label>
        <button type="submit" disabled={busy} className="btn btn--primary site-create__btn">
          <GlobeIcon /> {busy ? 'Adding…' : 'Add site'}
        </button>
      </form>
      {err && <p style={{ color: 'var(--danger-700)', marginTop: 12 }}>{err}</p>}
      <style>{SITE_CREATE_CSS}</style>
    </section>
  );
}

/**
 * Per-site row + an InlineAddForm under it for adding one more
 * origin to the site's allow-list.  Mirrors the card 059 pattern:
 * "Add a site origin" is a one-input + button surface, button aligned
 * to the input baseline.
 */
function SiteRow({ site, api, ask, onChanged }) {
  const [adding, setAdding] = useState(false);
  const origins = site.origins || [];

  async function patchOrigins(nextOrigins) {
    await api(`/v1/admin/sites/${site.site_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ origins: nextOrigins }),
    });
    onChanged();
  }

  async function addOrigin(origin) {
    await patchOrigins(Array.from(new Set([...origins, origin])));
  }

  function removeOrigin(origin) {
    const next = origins.filter((o) => o !== origin);
    const isLast = next.length === 0;
    ask({
      title: isLast
        ? `Remove the last origin from "${site.name}"?`
        : `Remove ${origin}?`,
      body: isLast
        ? 'The site will reject every request until you add a new origin. Use Delete to drop the site entirely.'
        : `Requests from ${origin} stop being allowed immediately. Other origins on this site are unaffected.`,
      confirmLabel: 'Remove',
      danger: true,
      runOnConfirm: async () => { await patchOrigins(next); },
    });
  }

  return (
    <li className="list__row list__row--column" style={{ display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div className="list__primary">
          <span className="list__name">
            {site.name}
            {!site.active && <span className="badge badge--default" style={{ marginLeft: 8 }}>Inactive</span>}
          </span>
          {origins.length === 0 ? (
            <span className="list__meta">no origins yet</span>
          ) : (
            <ul className="site-row__origins">
              {origins.map((o) => (
                <li key={o} className="site-row__origin">
                  <span className="site-row__origin-url">{o}</span>
                  <button
                    type="button"
                    className="site-row__origin-remove"
                    onClick={() => removeOrigin(o)}
                    aria-label={`Remove ${o}`}
                    title="Remove origin"
                  >
                    <XIcon />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="row">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setAdding((a) => !a)}
          >
            <AddIcon /> {adding ? 'Hide' : 'Add origin'}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => ask({
              title: `Delete site "${site.name}"?`,
              body: 'Origins stop being allowed immediately.',
              confirmLabel: 'Delete',
              danger: true,
              runOnConfirm: async () => {
                await api(`/v1/admin/sites/${site.site_id}`, { method: 'DELETE' });
                onChanged();
              },
            })}
          >
            <DeleteIcon /> Delete
          </button>
        </div>
      </div>
      {adding && (
        <div style={{ marginTop: 10 }}>
          <InlineAddForm
            label="New origin"
            placeholder="https://app.acme.com"
            buttonLabel="Add origin"
            icon={<GlobeIcon />}
            type="url"
            hint="HTTPS only.  Wildcards aren't supported — register each subdomain explicitly."
            validate={(v) => /^https:\/\/.+\..+/.test(v)}
            onSubmit={async (v) => { await addOrigin(v); setAdding(false); }}
          />
        </div>
      )}
      <style>{SITE_ROW_CSS}</style>
    </li>
  );
}

const SITE_ROW_CSS = `
  .site-row__origins {
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .site-row__origin {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 4px 3px 10px;
    background: var(--surface-1);
    border: 1px solid var(--border-1);
    border-radius: 999px;
    font-size: 12px;
    color: var(--text-2);
    line-height: 1;
  }
  .site-row__origin-url {
    font-variant-numeric: tabular-nums;
    word-break: break-all;
  }
  .site-row__origin-remove {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--text-3);
    cursor: pointer;
    border-radius: 50%;
  }
  .site-row__origin-remove:hover {
    background: var(--danger-50, rgba(220, 38, 38, 0.1));
    color: var(--danger-700, #b91c1c);
  }
  .site-row__origin-remove svg {
    width: 12px;
    height: 12px;
  }
`;

const SITE_CREATE_CSS = `
  .site-create {
    display: grid;
    grid-template-columns: 1fr 2fr auto;
    column-gap: 12px;
    align-items: end;
    margin-top: 10px;
  }
  .site-create__field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  /* The .btn class already sets a 32-34px height; align-items: end
     on the grid lines its bottom up with the input bottom — the
     "vertically centred between label + input" anti-pattern called
     out in card 059 stays fixed. */
  .site-create__btn { align-self: end; white-space: nowrap; }
  @media (max-width: 720px) {
    .site-create { grid-template-columns: 1fr; }
    .site-create__btn { width: 100%; justify-content: center; }
  }
`;
